import { useContext } from 'react';
import { Controller } from 'react-hook-form';
import Select from 'react-select';
import {
  Col,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  Label,
} from 'reactstrap';
import InputField from '../../../../components/layout/InputField';
import { AppContext } from '../../../../context';
import { PLACEMENT_COMISSION } from '../../../../utils/constant';
import {
  commaRemover,
  localStringMaker,
  moneyFormatter,
  moneyFieldHandler,
} from '../../../../utils/helper';

export default function DirectHirePlacementTypeBase({
  control,
  _getCompanyAgreementsLoading,
  salaryType,
  register,
  readonly,
  watch,
  agreements,
  localSalary,
  errors,
  setValue,
  setSalaryType,
  disableAllFields,
}: any) {
  const { theme } = useContext(AppContext);

  const onAgreementFilling = ({ value }: { value: string }) => {
    const oneAgreement = agreements?.find((item) => item?.id === value);
    if (
      oneAgreement?.agreementType === 'DIRECT_HIRE' ||
      oneAgreement?.agreementType === 'LEGAL_DIRECT_HIRE' ||
      oneAgreement?.agreementType === 'CUSTOM'
    ) {
      // Resetting the values
      const totalFeeCalculations =
        watch('commissionType')?.value === 'FLAT_FEE'
          ? localStringMaker(oneAgreement?.commissionPercentage)
          : localStringMaker(
            commaRemover(watch('salary')) *
            oneAgreement?.commissionPercentage *
            (1 / 100)
          );

      const netCommission =
        watch('adjustmentAmount') !== '0.00'
          ? localStringMaker(
            commaRemover(totalFeeCalculations) -
            commaRemover(watch('adjustmentAmount'))
          )
          : totalFeeCalculations;

      setValue('commissionType', { label: 'Percentage', value: 'PERCENTAGE' });
      setValue('totalFee', totalFeeCalculations);
      setValue('netCommission', netCommission);
      setValue('payableDays', oneAgreement?.payableDays?.toString());
      setValue('replacementDays', oneAgreement?.replacementDays?.toString());
      setValue(
        'feeStructured',
        localStringMaker(oneAgreement?.commissionPercentage)
      );
    }
  };

  const customStyles = {
    control: (styles, provided) => ({
      ...styles,

      backgroundColor: theme === 'light' ? provided.background : '#282828',
      borderColor: theme === 'light' ? '#ced4da' : '#525252',
      color: theme === 'light' ? provided.backgroundColor : '#d9d9d9',
      fontSize: 12,
      '&:hover': {
        borderColor: theme === 'light' ? '#ced4da' : '#282828',
        color: theme === 'light' ? provided.background : '#d9d9d9',
      },
    }),
    menu: (provided) => ({
      ...provided,
      background: theme === 'light' ? provided.background : '#282828',
      color: theme === 'light' ? provided.background : '#d9d9d9',
    }),
    option: (provided) => ({
      ...provided,
      fontSize: 12,
      background: theme === 'light' ? provided.background : '#363636',
      '&:hover': {
        background: theme === 'light' ? provided.background : '#474444',
        color: theme === 'light' ? provided.background : '#d9d9d9',
      },
    }),
    container: (provided, state) => ({
      ...provided,
      fontSize: 12,
    }),
  };

  const calculateTotalFee = () => {
    let commissionType = watch('commissionType')?.value;
    let salaryType = watch('salaryType');
    let salary = watch('salary');
    let feeStructured = watch('feeStructured');

    let total =
      commissionType === 'FLAT_FEE'
        ? localStringMaker(commaRemover(feeStructured))
        : localStringMaker(
          commaRemover(
            salaryType === 'ANNUALLY'
              ? salary
              : localStringMaker(commaRemover(salary) * 40 * 52)
          ) *
          commaRemover(feeStructured) *
          (1 / 100)
        );

    return total;
  };

  return (
    <>
      <Col lg="4" md="4" sm="12">
        <Label>Salary Type</Label>

        <div className="relocate-allowed">
          <FormGroup check className="p-0">
            <Controller
              control={control}
              name="salaryType"
              render={({ field }) => (
                <Label className="radio-container radio-font">
                  Annually
                  <Input
                    type="radio"
                    value="ANNUALLY"
                    checked={salaryType === 'ANNUALLY'}
                    onChange={(e) => {
                      field.onChange(e);
                      setSalaryType(e.target.value);
                      setValue('salaryType', e.target.value);
                    }}
                    disabled={disableAllFields || readonly}
                    innerRef={field.ref}
                  />
                  <span className="checkmark"></span>
                </Label>
              )}
            />
          </FormGroup>

          <FormGroup check>
            <Controller
              control={control}
              name="salaryType"
              render={({ field }) => (
                <Label className="radio-container radio-font">
                  Hourly
                  <Input
                    type="radio"
                    value="HOURLY"
                    checked={salaryType === 'HOURLY'}
                    onChange={(e) => {
                      field.onChange(e);
                      setSalaryType(e.target.value);
                      setValue('salaryType', e.target.value);
                    }}
                    disabled={disableAllFields || readonly}
                    innerRef={field.ref}
                  />
                  <span className="checkmark"></span>
                </Label>
              )}
            />
          </FormGroup>
        </div>
      </Col>

      <Col
        lg="4"
        md="4"
        sm="12"
        className={`${!readonly && 'dollar-addon-btn'}`}
      >
        <FormGroup>
          <Label>
            Salary <span className="text-danger">*</span>
          </Label>

          <InputGroup>
            <InputGroupAddon
              addonType="prepend"
              className={`${disableAllFields && 'diable-form-group'}`}
            >
              $
            </InputGroupAddon>
            <Controller
              control={control}
              name="salary"
              rules={{
                required: "salary is required!",
              }}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    {...field}
                    readOnly={readonly}
                    disabled={disableAllFields}
                    placeholder={'Salary'}
                    defaultValue={localSalary}
                    type="text"
                    onChange={(e) => {
                      if (e.target.value) {
                        moneyFieldHandler(e.target.value, setValue, 'salary');
                      } else {
                        field.onChange(e);
                      }
                    }}
                    invalid={!!fieldState.error}
                  />
                </>
              )}
            />
          </InputGroup>
          {errors?.salary && <FormFeedback className="d-block">{errors.salary.message as string}</FormFeedback>}

          <small className="text-muted ">
            {salaryType === 'HOURLY'
              ? `Estimated annual salary will be ${moneyFormatter(
                commaRemover(watch('salary')) * 40 * 52
              )}`
              : null}
          </small>
        </FormGroup>
      </Col>

      <Col lg="4" md="4" sm="12" className="agreementSelectSpacing">
        <Label>
          Agreement <span className="text-danger">*</span>
        </Label>
        <Controller
          rules={{
            required: {
              value: true,
              message: 'Agreement must be selected!',
            },
          }}
          name="agreement"
          render={({ field: { onChange, name, value, ref } }) => (
            <Select
              isDisabled={disableAllFields || readonly}
              isLoading={_getCompanyAgreementsLoading}
              onChange={(e) => {
                onAgreementFilling(e);
                onChange(e);
              }}
              value={value?.value ? value : null}
              name={name}
              ref={ref}
              styles={customStyles}
              options={agreements?.map((item: any) => {
                return {
                  label: item?.nickName,
                  value: item?.id,
                };
              })}
            />
          )}
          control={control}
        />
        {errors?.agreement && <small className="text-danger">{errors?.agreement?.message as any}</small>}
      </Col>

      <Col lg="4" md="4" sm="12" className="agreementSelectSpacing">
        <Label>
          Commission Type <span className="text-danger">*</span>
        </Label>
        <Controller
          rules={{
            required: {
              value: true,
              message: 'commission Type must be selected!',
            },
          }}
          control={control}
          name="commissionType"
          defaultValue={{ label: 'Flat Fee', value: 'FLAT_FEE' }}
          render={({ field: { onChange, name, value, ref } }) => (
            <Select
              isDisabled={disableAllFields || readonly}
              onChange={(e) => {
                onChange(e);
              }}
              styles={customStyles}
              value={value}
              name={name}
              ref={ref}
              options={PLACEMENT_COMISSION}
            />
          )}
        />
        {errors?.commissionType && <small className="text-danger">{errors?.commissionType?.message as any}</small>}
      </Col>

      <Col
        lg="4"
        md="4"
        sm="12"
        className={`${!readonly && 'dollar-addon-btn'}`}
      >
        <div className="agreementSelectSpacing">
          <Label>
            Fee <span className="text-danger">*</span>
          </Label>
          <InputGroup>
            <InputGroupAddon
              addonType="prepend"
              className={`${disableAllFields && 'diable-form-group'}`}
            >
              {watch('commissionType')?.value === 'FLAT_FEE' ? '$' : '%'}
            </InputGroupAddon>
            <Controller
              control={control}
              name="feeStructured"
              rules={{
                required: 'Fee is required',
              }}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    {...field}
                    readOnly={readonly}
                    disabled={disableAllFields}
                    placeholder={`Fee ${watch('commissionType')?.value === 'FLAT_FEE'
                      ? 'in USD'
                      : 'in %'
                      }`}
                    onChange={(e) => {
                      if (e.target.value) {
                        if (watch('commissionType')?.value === 'FLAT_FEE') {
                          moneyFieldHandler(
                            e?.target?.value,
                            setValue,
                            'feeStructured'
                          );
                        } else {
                          setValue('feeStructured', e?.target?.value);
                        }
                      } else {
                        field.onChange(e);
                      }
                    }}
                    invalid={!!fieldState.error}
                  />
                </>
              )}
            />
          </InputGroup>
          {errors?.feeStructured && <FormFeedback className="d-block">{errors.feeStructured.message as string}</FormFeedback>}
        </div>
      </Col>

      <Col lg="4" md="4" sm="12" className="agreementSelectSpacing">
        <Controller
          control={control}
          name="replacementDays"
          rules={{
            required: {
              value: true,
              message: 'Replacement days is required!',
            },
            minLength: {
              value: 1,
              message: "Length of replacement days can't be less than 1",
            },
            maxLength: {
              value: 3,
              message: "Length of replacement days can't be more than 3",
            },
          }}
          render={({ field, fieldState }) => (
            <InputField
              {...field}
              readOnly={readonly}
              disabled={disableAllFields}
              min="0"
              label="Replacement Days"
              isRequired
              inputtype="text"
              placeholder="e.g. 100"
              inputid="replacementDays"
              errMsg={fieldState.error?.message}
            />
          )}
        />
      </Col>

      <Col lg="4" md="4" sm="12" className="agreementSelectSpacing">
        <Controller
          control={control}
          name="payableDays"
          rules={{
            required: {
              value: true,
              message: 'Payable days is required!',
            },
            minLength: {
              value: 1,
              message: "Length of payable days can't be less than 1",
            },
            maxLength: {
              value: 2,
              message: "Length of payable days can't be more than 2",
            },
          }}
          render={({ field, fieldState }) => (
            <InputField
              {...field}
              label="Payable Days"
              isRequired
              disabled={disableAllFields}
              readOnly={readonly}
              inputtype="text"
              placeholder="e.g. 15"
              inputid="payableDays"
              errMsg={fieldState.error?.message}
            />
          )}
        />
      </Col>

      <Col lg="4" md="4" sm="12">
        <Controller
          control={control}
          name="adjustmentReason"
          rules={{
            required: {
              value: false,
              message: 'Adjustment reason is required!',
            },
          }}
          render={({ field, fieldState }) => (
            <InputField
              {...field}
              disabled={disableAllFields}
              readOnly={readonly}
              label="Adjustment Reason"
              inputtype="text"
              placeholder="Reason for adjustment amount"
              inputid="adjustmentReason"
              errMsg={fieldState.error?.message}
            />
          )}
        />
      </Col>

      <Col
        lg="4"
        md="4"
        sm="12"
        className={`${!readonly && 'dollar-addon-btn'}`}
      >
        <Label>Adjustment Amount</Label>
        <InputGroup>
          <InputGroupAddon addonType="prepend" className={'diable-form-group'}>$</InputGroupAddon>
          <Controller
            control={control}
            name="adjustmentAmount"
            rules={{
              required: {
                value: false,
                message: 'Adjustment amount is required!',
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <Input
                  {...field}
                  readOnly={readonly}
                  disabled={disableAllFields}
                  placeholder={'Adjustment Amount'}
                  onChange={(e) => {
                    if (e.target.value) {
                      moneyFieldHandler(
                        e.target.value,
                        setValue,
                        'adjustmentAmount'
                      );
                    } else {
                      field.onChange(e);
                    }
                  }}
                  invalid={!!fieldState.error}
                />
              </>
            )}
          />
        </InputGroup>
        {errors?.adjustmentAmount && <FormFeedback className="d-block">{errors.adjustmentAmount.message as string}</FormFeedback>}
      </Col>

      <Col lg="4" md="4" sm="12">
        <Label>Total Fee</Label>
        <InputGroup>
          <InputGroupAddon addonType="prepend" className={'diable-form-group'}>
            $
          </InputGroupAddon>
          <Controller
            control={control}
            name="totalFee"
            render={({ field }) => (
              <Input
                {...field}
                disabled={true}
                value={calculateTotalFee()}
                placeholder={`Total fee`}
              />
            )}
          />
        </InputGroup>
        {errors?.totalFee && <FormFeedback className="d-block">{errors.totalFee.message as string}</FormFeedback>}
      </Col>

      <Col lg="4" md="4" sm="12">
        <Label>Net Commission Fee</Label>
        <InputGroup>
          <InputGroupAddon addonType="prepend" className={'diable-form-group'}>
            $
          </InputGroupAddon>
          <Controller
            control={control}
            name="netCommission"
            render={({ field }) => (
              <Input
                {...field}
                disabled={true}
                value={
                  watch('adjustmentAmount') !== '0.00'
                    ? localStringMaker(
                      commaRemover(calculateTotalFee()) -
                      commaRemover(watch('adjustmentAmount'))
                    )
                    : calculateTotalFee() !== '0.00'
                      ? localStringMaker(commaRemover(calculateTotalFee()))
                      : '0.00'
                }
                placeholder={`Net fee`}
              />
            )}
          />
        </InputGroup>
        {errors?.netCommission && <FormFeedback className="d-block">{errors.netCommission.message as string}</FormFeedback>}
      </Col>
    </>
  );
}
